import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import pe from "./pe.json";
import en from "./en.json";

const DICTIONARIES = { pe, en };
export const SUPPORTED_LANGS = ["pe", "en"];
export const DEFAULT_LANG = "pe";

const I18nContext = createContext(null);

function getFromPath(dict, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc === undefined || acc === null) return undefined;
    const idxMatch = key.match(/^(.+)\[(\d+)\]$/);
    if (idxMatch) {
      const arr = acc[idxMatch[1]];
      if (!Array.isArray(arr)) return undefined;
      return arr[parseInt(idxMatch[2], 10)];
    }
    return acc[key];
  }, dict);
}

export function detectLangFromPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0];
  if (SUPPORTED_LANGS.includes(seg)) return seg;
  return null;
}

export function I18nProvider({ lang, children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const value = useMemo(() => {
    const dict = DICTIONARIES[lang] || DICTIONARIES[DEFAULT_LANG];
    const t = (key, fallback = "") => {
      const val = getFromPath(dict, key);
      return val === undefined || val === null ? fallback || key : val;
    };
    const tArray = (key) => {
      const val = getFromPath(dict, key);
      return Array.isArray(val) ? val : [];
    };
    const switchLang = (nextLang) => {
      if (!SUPPORTED_LANGS.includes(nextLang) || nextLang === lang) return;
      const parts = location.pathname.split("/").filter(Boolean);
      if (parts[0] && SUPPORTED_LANGS.includes(parts[0])) {
        parts[0] = nextLang;
      } else {
        parts.unshift(nextLang);
      }
      const nextPath = "/" + parts.join("/") + (location.search || "");
      navigate(nextPath, { replace: false });
    };
    const localePath = (path = "") => {
      const clean = path.startsWith("/") ? path : `/${path}`;
      return `/${lang}${clean === "/" ? "" : clean}`;
    };
    return { lang, t, tArray, switchLang, localePath };
  }, [lang, location.pathname, location.search, navigate]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    return {
      lang: DEFAULT_LANG,
      t: (k) => k,
      tArray: () => [],
      switchLang: () => {},
      localePath: (p) => `/${DEFAULT_LANG}${p}`,
    };
  }
  return ctx;
}

export function getStaticDict(lang) {
  return DICTIONARIES[lang] || DICTIONARIES[DEFAULT_LANG];
}
